/**
 * Keyboard Shortcuts Hook
 * Provides global keyboard shortcut handling with Mac/Windows support.
 * WCAG 2.1 AA compliant - shortcuts don't conflict with assistive technology.
 * @packageDocumentation
 */

import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * Interface defining a keyboard shortcut with modifiers and handler.
 */
export interface KeyboardShortcut {
  key: string;
  scope?: string;
  altKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  description: string;
  preventDefault?: boolean;
  handler: (event: KeyboardEvent) => void;
}

/**
 * Options for configuring keyboard shortcuts behavior.
 */
export interface UseKeyboardShortcutsOptions {
  scope?: string;
  enabled?: boolean;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/i.test(navigator.userAgent);

/**
 * Hook to register and handle keyboard shortcuts with scope and modifier support.
 *
 * @param { KeyboardShortcut[] } shortcuts Array of keyboard shortcuts to register.
 * @param { UseKeyboardShortcutsOptions } options Configuration options.
 * @returns { void }
 */
export const useKeyboardShortcuts = (shortcuts: KeyboardShortcut[], options: UseKeyboardShortcutsOptions = {}) => {
  const { scope, enabled = true } = options;
  const shortcutsRef = useRef(shortcuts);

  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled || !event.key) {
        return;
      }

      const target = event.target as HTMLElement;
      const isEditing = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      for (const shortcut of shortcutsRef.current) {
        if (scope && shortcut.scope && shortcut.scope !== scope) {
          continue;
        }

        const modifierKey = isMac ? shortcut.metaKey : shortcut.ctrlKey;
        const eventModifierKey = isMac ? event.metaKey : event.ctrlKey;

        const matchesKey = event.key.toLowerCase() === shortcut.key.toLowerCase();
        const matchesModifier = modifierKey ? eventModifierKey : !eventModifierKey;
        const matchesShift = shortcut.shiftKey ? event.shiftKey : !event.shiftKey;
        const matchesAlt = shortcut.altKey ? event.altKey : !event.altKey;

        if (matchesKey && matchesModifier && matchesShift && matchesAlt) {
          if (isEditing && !modifierKey) {
            continue;
          }

          if (shortcut.preventDefault !== false) {
            event.preventDefault();
          }

          shortcut.handler(event);

          return;
        }
      }
    },
    [enabled, scope],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handleKeyDown]);
};

/**
 * Function to format a keyboard shortcut into human-readable text.
 *
 * @param { KeyboardShortcut } shortcut The shortcut to format.
 * @returns { string } Returns formatted shortcut string.
 */
export const formatShortcut = (shortcut: KeyboardShortcut): string => {
  const parts: string[] = [];

  if (isMac) {
    if (shortcut.ctrlKey || shortcut.metaKey) {
      parts.push('\u2318');
    }

    if (shortcut.altKey) {
      parts.push('\u2325');
    }

    if (shortcut.shiftKey) {
      parts.push('\u21E7');
    }
  } else {
    if (shortcut.ctrlKey || shortcut.metaKey) {
      parts.push('Ctrl');
    }

    if (shortcut.altKey) {
      parts.push('Alt');
    }

    if (shortcut.shiftKey) {
      parts.push('Shift');
    }
  }

  const keyDisplay = shortcut.key.length === 1 ? shortcut.key.toUpperCase() : shortcut.key;
  parts.push(keyDisplay);

  return parts.join(isMac ? '' : '+');
};

/**
 * Interface for displaying keyboard shortcuts in help documentation.
 */
export interface KeyboardShortcutsHelpItem {
  key: string;
  scope: string;
  description: string;
}

/**
 * Hook to manage keyboard shortcuts help dialog state.
 *
 * @returns { { isOpen: boolean; open: () => void; close: () => void; toggle: () => void } } Returns dialog state and control functions.
 */
export const useKeyboardShortcutsHelp = (): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
} => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  /**
   * Listen for global event to show keyboard shortcuts help dialog.
   * This allows any shortcut to trigger the help dialog by dispatching the event.
   */
  useEffect(() => {
    // Handler to open the shortcuts help dialog when the event is dispatched
    const handleShowShortcuts = () => setIsOpen(true);

    // Listen for the custom event that triggers the shortcuts help dialog
    globalThis.addEventListener('show-keyboard-shortcuts', handleShowShortcuts);

    return () => globalThis.removeEventListener('show-keyboard-shortcuts', handleShowShortcuts);
  }, []);

  return {
    open,
    close,
    isOpen,
    toggle,
  };
};

export const GLOBAL_SHORTCUTS: KeyboardShortcut[] = [
  {
    key: 's',
    ctrlKey: true,
    metaKey: true,
    scope: 'editor',
    description: 'Save current changes',
    handler: () => {
      const event = new CustomEvent('save-changes');

      globalThis.dispatchEvent(event);
    },
  },
  {
    key: 'k',
    ctrlKey: true,
    metaKey: true,
    scope: 'global',
    description: 'Open command palette / search',
    handler: () => {
      const searchInput = document.querySelector<HTMLInputElement>('[data-search-input]');

      searchInput?.focus();
    },
  },
  {
    key: '/',
    ctrlKey: true,
    metaKey: true,
    scope: 'global',
    description: 'Show keyboard shortcuts',
    handler: () => {
      const event = new CustomEvent('show-keyboard-shortcuts');

      globalThis.dispatchEvent(event);
    },
  },
  {
    key: 'F1',
    scope: 'global',
    description: 'Open help panel',
    handler: () => {
      const event = new CustomEvent('toggle-help-panel');

      globalThis.dispatchEvent(event);
    },
  },
  {
    key: 'Escape',
    scope: 'global',
    preventDefault: false,
    description: 'Close modal/panel',
    handler: () => {
      const event = new CustomEvent('close-modal');

      globalThis.dispatchEvent(event);
    },
  },
];

/**
 * Hook to register all global keyboard shortcuts for the application.
 *
 * @returns { KeyboardShortcut[] } Returns array of global shortcuts.
 */
export const useGlobalShortcuts = () => {
  useKeyboardShortcuts(GLOBAL_SHORTCUTS);

  return GLOBAL_SHORTCUTS;
};
